import { ComponentType } from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import {
  StyledDefaultTableCell as DefaultTableCell,
  StyledDealNameCell as DealNameCell,
  StyledPropertyTypeCell as PropertyTypeCell,
  StyledCurrencyCell as CurrencyCell,
  StyledSmallDateCell as SmallDateCell,
  StyledLargeDateCell as LargeDateCell,
  StyledRateTypeCell as RateTypeCell,
  StyledPercentageCell as RateCell,
  StyledNumericCell as NumberCell,
  StyledBooleanCell as BooleanCell
} from "./GridCells";

export interface ColumnInterface {
  title?: string;
  field?: string;
  default?: boolean;
  cell?: ComponentType<GridCellProps>;
  filter?: "boolean" | "numeric" | "text" | "date";
  format?: string;
  minWidth?: number;
  minGridWidth?: number;
  locked?: boolean;
  width?: string | number;
  reorderable?: boolean;
  exportCellFormat?: string;
}

// This array lists every possible column that could appear in the Broker Deals table.
// It does not specify which columns are currently visible, nor anything about their position or data.
const tableColumns: ColumnInterface[] = [
  {
    title: "Client",
    field: "clientName",
    default: true,
    cell: DefaultTableCell,
    filter: "text",
    width: 150
  },
  {
    title: "Deal",
    field: "dealName",
    default: true,
    cell: DealNameCell,
    filter: "text",
    width: 217
  },
  {
    title: "Location",
    field: "propertyLocation",
    default: true,
    cell: DefaultTableCell,
    filter: "text",
    width: 120
  },
  {
    title: "Type",
    field: "propertyAssetType",
    default: true,
    cell: PropertyTypeCell,
    filter: "text",
    width: 100
  },
  {
    title: "Maturing",
    field: "maturityDate",
    default: true,
    cell: SmallDateCell,
    filter: "date",
    width: 110,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Lender",
    field: "lenders",
    default: true,
    cell: DefaultTableCell,
    filter: "text",
    width: 150
  },
  {
    title: "Balance",
    field: "balance",
    default: true,
    cell: CurrencyCell,
    filter: "numeric",
    width: 100,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "DY",
    field: "debtYield",
    filter: "numeric",
    default: true,
    cell: RateCell,
    width: 80,
    exportCellFormat: "0.00%"
  },
  {
    title: "NOI",
    field: "noiActual",
    default: true,
    cell: CurrencyCell,
    filter: "numeric",
    width: 90,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Rate",
    field: "allInRate",
    default: true,
    cell: RateCell,
    filter: "numeric",
    width: 80,
    exportCellFormat: "0.00%"
  },
  {
    title: "Type",
    field: "rateType",
    default: true,
    cell: RateTypeCell,
    filter: "text",
    width: 80
  },
  {
    title: "Prepay",
    field: "prepaymentPenalty",
    default: true,
    cell: CurrencyCell,
    filter: "numeric",
    width: 90,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Loan Type",
    field: "loanType",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 120
  },
  {
    title: "Market Value",
    field: "marketValue",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 120,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Close",
    field: "closingDate",
    default: false,
    cell: LargeDateCell,
    filter: "date",
    width: 120,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Amort Start",
    field: "amortizationStartDate",
    default: false,
    cell: LargeDateCell,
    filter: "date",
    width: 120,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Prepay Type",
    field: "prepaymentTypes",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 120
  },
  {
    title: "DSCR",
    field: "dscrActual",
    default: false,
    cell: NumberCell,
    filter: "numeric",
    width: 120,
    exportCellFormat: "0.00"
  },
  {
    title: "LTV",
    field: "ltv",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 100,
    exportCellFormat: "0.00%"
  },
  {
    title: "Units",
    field: "units",
    default: false,
    cell: NumberCell,
    filter: "numeric",
    width: 100
  },
  {
    title: "SQ Feet",
    field: "squareFeet",
    default: false,
    cell: NumberCell,
    filter: "numeric",
    width: 100,
    exportCellFormat: "#,##0"
  },
  {
    title: "Loan Amount",
    field: "loanAmount",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 120,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Amort Type",
    field: "amortizationType",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 120
  },
  {
    title: "Spread",
    field: "spread",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 100,
    exportCellFormat: "0.00%"
  },
  {
    title: "Index",
    field: "index",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 100
  },
  {
    title: "Broker Company",
    field: "brokerCompany",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 140
  },
  {
    title: "Purchase Date",
    field: "purchaseDate",
    default: false,
    cell: SmallDateCell,
    filter: "date",
    width: 130,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "NOI as-of Date",
    field: "noiAsOfDate",
    default: false,
    cell: SmallDateCell,
    filter: "date",
    width: 130,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Occupancy",
    field: "occupancy",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 120
  },
  {
    title: "Occupancy as-of Date",
    field: "occupancyAsOfDate",
    default: false,
    cell: SmallDateCell,
    filter: "date",
    width: 180,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Market Value as-of Date",
    field: "marketValueAsOfDate",
    default: false,
    cell: SmallDateCell,
    filter: "date",
    width: 180,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "MSA",
    field: "metropolitanStatisticalArea",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 100
  },
  {
    title: "Region",
    field: "region",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 100
  },
  {
    title: "Data Source",
    field: "dataSource",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 150
  },
  {
    title: "Data Source As-Of Date",
    field: "dataSourceAsOfDate",
    default: false,
    cell: SmallDateCell,
    filter: "date",
    width: 180,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Provided Balance",
    field: "providedBalance",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 160,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Implied Cap Strike",
    field: "impliedCapStrike",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 150,
    exportCellFormat: "0.00%"
  },
  {
    title: "Max Note Rate",
    field: "maxNoteRate",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 130,
    exportCellFormat: "0.00%"
  },
  {
    title: "Underwritten NOI",
    field: "underwrittenNoi",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 160,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Originator Company",
    field: "originatorCompany",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 160
  },
  {
    title: "Originator Broker",
    field: "originatorBroker",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 160
  },
  {
    title: "Net Cash Flow",
    field: "netCashflow",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 130,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Underwritten NCF",
    field: "underwrittenNetCashflow",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 150,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Effective Gross Income",
    field: "effectiveGrossIncome",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 180,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Market Value % Change from Appraised Value",
    field: "marketValueChangeFromAppraisedValue",
    default: false,
    cell: RateCell,
    filter: "numeric",
    width: 310,
    exportCellFormat: "0.00%"
  },
  {
    title: "Non Annualized Net Operating Income",
    field: "nonAnnualizedNetOperatingIncome",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 160,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Non Annualized Net Cashflow",
    field: "nonAnnualizedNetCashflow",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 160,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Reported NOI Calculation Methodology",
    field: "noiMethodology",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 300
  },
  {
    title: "Reported NCF Calculation Methodology",
    field: "ncfMethodology",
    default: false,
    cell: DefaultTableCell,
    filter: "text",
    width: 300
  },
  {
    title: "Refi Proceeds (Fixed)",
    field: "refiProceedsFixed",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 160,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Refi Proceeds (Floating)",
    field: "refiProceedsFloating",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 200,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Net Cash Diff. (Fixed)",
    field: "netCashDifferenceFixed",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 180,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Net Cash Diff. (Floating)",
    field: "netCashDifferenceFloating",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 200,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "ARA",
    field: "appraisalReductionAct",
    cell: CurrencyCell,
    filter: "numeric",
    width: 100,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "ARA Date",
    field: "appraisalReductionActDate",
    default: false,
    cell: LargeDateCell,
    filter: "date",
    width: 120,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Months Delinq.",
    field: "monthDelinquent",
    default: false,
    cell: NumberCell,
    filter: "numeric",
    width: 140,
    exportCellFormat: "0.00"
  },
  {
    title: "On Watch",
    field: "onWatch",
    default: true,
    cell: BooleanCell,
    filter: "boolean",
    width: 120
  },
  {
    title: "Cap Escrow Amount",
    field: "capEscrowAmount",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 180,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Cap Reserve Balance",
    field: "capReserveBalance",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 180,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Replacement Cap Cost",
    field: "replacementCapCost",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 180,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Replacement Cap Cost As of Date",
    field: "replacementCapAsOfDate",
    default: false,
    cell: LargeDateCell,
    filter: "date",
    width: 260,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Replacement Cap Term",
    field: "replacementCapTerm",
    default: false,
    cell: NumberCell,
    filter: "numeric",
    width: 220,
    exportCellFormat: "0.00"
  },
  {
    title: "Next Escrow Calculation",
    field: "nextEscrowCalculation",
    default: false,
    cell: LargeDateCell,
    filter: "date",
    width: 220,
    exportCellFormat: "m/d/yy"
  },
  {
    title: "Proj. Cap Reserve Balance",
    field: "projectedReserveBalance",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 220,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Cap Mark-to-Market",
    field: "capMarkToMarket",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 220,
    exportCellFormat: "$#,##0.00"
  },
  {
    title: "Replacement Cap Escrow Requirement",
    field: "capEscrowRequirement",
    default: false,
    cell: CurrencyCell,
    filter: "numeric",
    width: 220,
    exportCellFormat: "$#,##0.00"
  }
];

const defaultColumns = tableColumns.filter(column => column.default);

export { tableColumns, defaultColumns };
